import { Link } from 'react-router-dom';
import { Inbox, Paperclip } from 'lucide-react';
import { Skeleton, StatusBadge } from '@/design/components';
import { formatDate } from '@/lib/dates';
import {
  rangeLabel,
  requestStatusLabel,
  requestStatusTone,
  useAttachmentUrl,
  useEmployeeRequests,
  type HrRequest,
} from '@/features/requests/api';

/**
 * הבקשות של העובד, בתוך הכרטיס שלו (PRD 5.10.3).
 *
 * מנהל שפותח כרטיס עובד שואל "מה המצב איתו", ועד עכשיו התשובה
 * הזו היתה מפוזרת: הפרטים כאן, והבקשות במסך אחר שמסודר לפי זמן
 * ולא לפי אדם. שלוש חופשות ברצף הן עובדה על העובד הזה, ולא היה
 * מקום אחד שבו רואים אותה.
 *
 * קריאה בלבד. ההכרעה נשארת במסך הבקשות, שבו גם שדה ההערה ושורת
 * ההקשר של שאר הממתינות — כפתור אישור בודד כאן היה מפתה לאשר בלי
 * לראות מי עוד מבקש את אותם ימים.
 */
export function EmployeeRequests({ employeeId }: { employeeId: string }) {
  const requests = useEmployeeRequests(employeeId);

  if (requests.isPending) return <Skeleton className="h-16" />;
  // שקט ולא הודעת שגיאה: זו פיסה בתוך כרטיס, ונפילה שלה אינה
  // צריכה להסתיר את פרטי העובד
  if (requests.isError) return null;

  const rows = requests.data ?? [];
  const pending = rows.filter((request) => request.status === 'pending').length;

  return (
    <section className="grid gap-2 border-t border-line pt-3">
      <header className="flex flex-wrap items-center gap-2">
        <Inbox size={16} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        <h4 className="m-0 text-label font-medium text-ink">בקשות</h4>
        {pending > 0 && (
          <StatusBadge tone="warn">
            {pending === 1 ? 'אחת ממתינה' : `${pending} ממתינות`}
          </StatusBadge>
        )}
        <span className="grow" />
        {rows.length > 0 && (
          <Link to="/admin/employees/requests" className="text-label text-ink underline-offset-2">
            {pending > 0 ? 'לטיפול בבקשות' : 'לכל הבקשות'}
          </Link>
        )}
      </header>

      {rows.length === 0 ? (
        <p className="m-0 text-body-sm text-muted">לא הגיש/ה בקשות.</p>
      ) : (
        <ul className="m-0 grid list-none gap-1 p-0">
          {/* חמש האחרונות. כרטיס עובד אינו ארכיון, ומי שרוצה את
              הכל הולך למסך הבקשות */}
          {rows.slice(0, 5).map((request) => (
            <RequestRow key={request.id} request={request} />
          ))}
          {rows.length > 5 && (
            <li className="text-label text-muted">ועוד {rows.length - 5} קודמות.</li>
          )}
        </ul>
      )}
    </section>
  );
}

function RequestRow({ request }: { request: HrRequest }) {
  const attachment = useAttachmentUrl(request.attachment_path);
  const range = rangeLabel(request.from_date, request.to_date);

  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm">
      <StatusBadge tone={requestStatusTone[request.status]}>
        {requestStatusLabel[request.status]}
      </StatusBadge>
      <span className="text-ink">{request.hr_request_types?.name}</span>
      {range && <span className="tabular text-muted">{range}</span>}
      <span className="text-muted">· הוגשה {formatDate(request.created_at)}</span>
      {attachment.data && (
        <a
          href={attachment.data}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-ink"
        >
          <Paperclip size={14} strokeWidth={1.75} aria-hidden="true" />
          אישור
        </a>
      )}
    </li>
  );
}
